/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-table.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import { pick } from 'lodash';
import CopyButton from '@components/core/copy-button';
import { useBoolean, useTranslation } from 'hooks';
import { GET_DATA_REVIEW_TABLES, SAVE_DATA_REVIEW_TABLE } from 'queries/central-monitoring';
import { StudyFields } from 'queries/generated/fragments.generated';
import { DataReviewType, RowDataSourceTag } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { createArrayFromEnum } from 'utils/generator';
import { checkConfigIdErrors, checkIfNameExist, validateFields } from 'utils/validation';
import { DataReviewColumnConst } from 'views/components/admin/studies/data-review-tables/column-pick-const';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

interface IAddTable {
    open: boolean;
    onClose: (any: any) => any;
    currentTable?: any;
    study: StudyFields;
    tables: any[];
    isSysAdmin: boolean;
}

const initialState = {
    id: null,
    configId: null,
    active: false,
    name: '',
    type: '',
    dataSource: '',
    description: '',
};

const initialErrors = {
    configId: '',
    name: '',
    type: '',
    dataSource: '',
    description: '',
};

const AddTable = ({ open, onClose, currentTable, study, tables, isSysAdmin }: IAddTable) => {
    const { t } = useTranslation();
    const [saveTable] = useMutation(SAVE_DATA_REVIEW_TABLE);

    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);

    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors } as any);
    const [types, setTypes] = useState([]);

    useEffect(() => {
        setTypes(createArrayFromEnum(DataReviewType, '-REVIEW_TYPE'));
    }, []);

    useEffect(() => {
        if (open)
            setState({
                id: currentTable?.id ?? state.id,
                configId: currentTable?.configId ?? state.configId,
                active: currentTable?.active ?? state.active,
                name: currentTable?.name ?? state.name,
                type: currentTable?.type ?? state.type,
                dataSource:
                    (currentTable?.type ?? state.type) === DataReviewType.COUNTRY ||
                    (currentTable?.type ?? state.type) === DataReviewType.SITE
                        ? 'sitesAll'
                        : (currentTable?.type ?? state.type) === DataReviewType.SUBJECT
                          ? 'subjectsAll'
                          : (currentTable?.dataSource ?? state.dataSource),
                description: currentTable?.description ?? state.description,
            });
    }, [currentTable, open]);

    const close = (action?: string) => {
        if (action !== 'error') {
            setState(initialState);
            setErrors(initialErrors);
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        let error = validateFields(value, field, length, required);

        const configIdError = checkConfigIdErrors(state, tables, value);
        if (field === 'configId') {
            error = { configId: configIdError };
        }

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        let length = 600;

        if (name === 'name') length = 30;
        if (name === 'configId') length = 24;

        if (name === 'type' && event.target.value !== DataReviewType.DATA) {
            setState({
                ...state,
                [name]: event.target.value,
                dataSource:
                    event.target.value === DataReviewType.COUNTRY || event.target.value === DataReviewType.SITE
                        ? 'sitesAll'
                        : event.target.value === DataReviewType.SUBJECT
                          ? 'subjectsAll'
                          : '',
            });
            setErrors({
                ...errors,
                ...validateFields(event.target.value, name, length, false),
                dataSource: '',
            });
        } else {
            setState({
                ...state,
                [name]: event.target.value,
            });
            validate(event.target.value, name, length, false);
        }
    };

    const submit = () => {
        const error = {
            ...validateFields(state.name, 'name', 30, true),
            ...validateFields(state.type, 'type', undefined, true),
        } as any;

        if (currentTable) {
            const configIdError = checkConfigIdErrors(state, tables);

            if (configIdError) {
                error.configId = configIdError;
            }
        }

        error.dataSource =
            state.type === DataReviewType.DATA && state.dataSource === '' ? t('ra:requiredFieldError') : '';

        setErrors({
            ...errors,
            ...error,
        });

        const isErrors = Object.values(error).some((x) => x !== null && x !== '');

        const isTableNameExists = checkIfNameExist(
            {
                ...state,
                name: state.name.trim(),
                id: currentTable?.id ?? null,
            },
            tables,
            'name',
        );

        if (isTableNameExists) {
            setErrors({ ...errors, name: t('admin:tableNameExists') });
        }

        if (!isErrors && !isTableNameExists) {
            saving.setTrue();
            const columns =
                currentTable?.columns.map((item: any) => {
                    const col = pick(item, DataReviewColumnConst);
                    if (!col.columnId) col.columnId = Constants.ObjectId.Empty;

                    return col;
                }) ?? [];

            saveTable({
                variables: {
                    table: {
                        ...state,
                        studyId: study?.id,
                        columns,
                    },
                    reason: '',
                },
                refetchQueries: [
                    {
                        query: GET_DATA_REVIEW_TABLES,
                        variables: {
                            studyId: study?.id,
                        },
                    },
                ],
            })
                .then(() => {
                    saving.setFalse();
                    close(currentTable ? 'edit' : 'add');
                })
                .catch(() => {
                    saving.setFalse();
                    close('error');
                });
        }
    };

    return (
        <CustomDialog
            onClose={() => close()}
            aria-labelledby="add-table-title"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="add-table-title">
                {currentTable ? t('admin:editTableDialog', { id: currentTable?.recordId }) : t('admin:addTable')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingTop: 15 }}>
                <Checkbox
                    rounded="none"
                    className="cell-checkbox no-full-width flex-start no-border"
                    checked={state.active}
                    onChange={() => {
                        disabledSave.setFalse();
                        setState({
                            ...state,
                            active: !state.active,
                        });
                    }}
                    label={t<string>('active')}
                    disabled={isSysAdmin}
                    data-testid="active-checkbox"
                    size="medium"
                />
                <div className="selects-wrapper flex-end" style={{ paddingTop: 5 }}>
                    <FormControl variant="outlined" className="form-action-type mr">
                        <TextField
                            label={`${t('name')}*`}
                            id="edit-table-name"
                            fullWidth
                            value={state.name}
                            onChange={onChange('name')}
                            size="small"
                            placeholder={t('admin:enterName')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-table-name-text"
                                    length={state.name.length}
                                    max={30}
                                    error={errors.name}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.name}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    {!currentTable ? (
                        <div
                            style={{ paddingBottom: `${errors.name === t('admin:tableNameExists') ? '49px' : '29px'}` }}
                            className="form-action-type"
                        >
                            <SelectWithCategories
                                label={`${t('admin:dataBasedOn')}*`}
                                dataArray={types}
                                onChange={onChange}
                                fieldSelect="type"
                                currentState={state.type}
                                searchable={false}
                                filterable={false}
                                labelField="type"
                                showWarning={false}
                                customClassName="form-action-type"
                                error={errors.type}
                                disabled={isSysAdmin}
                            />
                        </div>
                    ) : null}
                </div>
                {!currentTable && state.type === DataReviewType.DATA ? (
                    <div
                        className="selects-wrapper not-full"
                        style={{ paddingBottom: errors.dataSource && errors.dataSource !== '' ? '19px' : '0px' }}
                    >
                        <DataSourceSelector
                            label={`${t('dataSource')}*`}
                            onChange={(value: string) => {
                                onChange('dataSource')({ target: { value } });
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="dataSource"
                            showDefault={false}
                            customClassName="form-action-type"
                            searchable
                            tag={RowDataSourceTag.DATA_TABLE_DATA_LISTING}
                            error={errors.dataSource}
                            disabled={isSysAdmin}
                        />
                    </div>
                ) : null}
                {!!currentTable && (
                    <div>
                        <div style={{ marginBottom: 10 }}>
                            <span className="title">{t('admin:dataBasedOn')}:</span>
                            {t(`admin:${state.type}-REVIEW_TYPE`)}
                        </div>
                        <div style={{ marginBottom: 15 }}>
                            <span className="title">{t('dataSource')}:</span>
                            {state.dataSource}
                        </div>
                    </div>
                )}
                <div className={cn('type-edc-wrapper', { 'mb-10': currentTable })}>
                    <TextField
                        label={t('description')}
                        id="edit-table-description"
                        multiline
                        rows={5}
                        fullWidth
                        value={state.description}
                        onChange={onChange('description')}
                        size="small"
                        placeholder={t('admin:enterDescription')}
                        helperText={
                            <CharactersRemaining
                                id="edit-table-description"
                                length={state.description.length}
                                max={600}
                                error={errors.description}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        error={!!errors.description}
                        disabled={isSysAdmin}
                    />
                </div>
                {!!currentTable && (
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type">
                            <TextField
                                id="edit-field-config-id"
                                label={t('admin:configId')}
                                onChange={onChange('configId')}
                                fullWidth
                                value={state.configId}
                                size="small"
                                slotProps={{
                                    input: {
                                        readOnly: !isSysAdmin,
                                        endAdornment: <CopyButton value={state.configId ?? ''} />,
                                    },
                                }}
                                helperText={
                                    <span style={{ color: '#ae0015' }} className="remaining-text">
                                        {errors.configId}
                                    </span>
                                }
                                variant="outlined"
                                color="secondary"
                                error={!!errors.configId}
                            />
                        </FormControl>
                    </div>
                )}
                <DialogActions className={`actions-info-wrapper-edit-cv ${!currentTable ? 'flex-end' : ''}`}>
                    <ModalUpdatedInfo item={currentTable} />
                    <SaveButton
                        className="edit-cv-update-btn"
                        id="edit-observation-save"
                        text={currentTable ? 'common:update' : 'create'}
                        disabled={
                            Object.keys(errors).filter((item) => errors[item] && errors[item].length > 0).length > 0 ||
                            disabledSave.value ||
                            saving.value
                        }
                        onClick={submit}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddTable;
